import React, {Fragment} from 'react'
import styles from './UpgradeModal.scss'
import {ModalOverlay} from 'common/styleguide/ModalOverlay'
import {CTA} from 'common/styleguide/CTA'
import _ from 'lodash'
import {getCurrentUser} from 'lib/utils'

export const UPGRADE_MODAL = 'UPGRADE_MODAL'

const featureData = {
  advancedRouteEditing: {
    name: 'Advanced Route Editing',
    description: 'Turn ride files into routes with cuesheets, split and combine routes, and plot multi-modal trips to design the perfect route.',
    assetType: 'webm',
    helpPath: '/help/advanced-planning-and-editing',
    premium: true
  },
  pdfMapsAndCuesheets: {
    name: 'PDF Maps & Cuesheets',
    description: 'Craft the perfect map with multiple size and orientation options, ordered points of interest, custom cues, and multiple map styles to choose from.',
    assetType: 'png',
    helpPath: '/help/create-customize-a-cuesheet'
  },
  rideCleanup: {
    name: 'Ride Cleanup',
    description: 'Remove bad data, trim ends, exclude sections from metrics, or join multiple rides together. Ride Cleanup Tools always ensure accurate data.',
    assetType: 'webm',
    helpPath: '/help/ride-clean-up',
    premium: true
  },
  rideCenter: {
    name: 'Ride Center',
    description: 'Graphs and summaries of your data. Sort, filter, tag, and bulk edit your rides. It’s all here in the Ride Center.',
    assetType: 'webm',
    helpPath: '/help/ride-center',
    premium: true
  },
  estimatedTime: {
    name: 'Estimated Time',
    description: 'Know how long a ride is going to take with personalized estimated time for every route you create or find.',
    assetType: 'png',
    helpPath: '/help/estimated-time'
  },
  privateSegments: {
    name: 'Private Segments',
    description: 'Keep all your rides private and still track your progress with your own personal leaderboards and private segments.',
    assetType: 'png',
    helpPath: '/help/private-segments',
    premium: true
  },
  mobileNavigation: {
    name: 'Mobile Navigation',
    description: 'Spoken turn-by-turn directions and visual cues let you keep you head up and enjoy the ride.',
    assetType: 'png',
    helpPath: '/help/voice-navigation'
  },
  offlineMaps: {
    name: 'Offline Maps',
    description: 'No cell connection? No problem. Save battery and data with offline maps. Get our voice navigation and maps anywhere you go, even with your phone in airplane mode.',
    assetType: 'png',
    helpPath: '/help/offline-maps-iphone'
  },
  liveLogging: {
    name: 'Live Logging',
    description: 'Share your rides in real time with friends and family. Let them track your progress, and even type comments which our mobile app will read aloud to you.',
    assetType: 'png',
    helpPath: '/help/live-logging'
  },
  maintenanceLog: {
    name: 'Maintenance Log',
    description: 'Keep your bike in tip-top shape by planning and tracking your maintenance.',
    assetType: 'png',
    helpPath: '/help/bicycle-maintenance-log'
  },
  deviceExport: {
    name: 'Device Export',
    description: 'After you have planned your route, export it to your Garmin device for navigation.',
    assetType: 'png',
    helpPath: '/help/export-routes-to-garmin-device'
  },
  leaderboardFiltering: {
    name: 'Event Leaderboard Filtering',
    description: 'The overall leaderboard will show all matched participants and rank them for each segment across the entire event.',
    assetType: 'png',
    helpPath: '/help/event-leaderboards',
    premium: true
  },
  stationaryRides: {
    name: 'Stationary Rides',
    description: 'Use the mobile app for iPhone and Android, or your Garmin unit to record your ride to get the moving time, heart rate, speed, candence and/or power data, and we’ll track it with the rest of your rides.',
    assetType: 'png',
    helpPath: '/help/stationary-ride-support',
    premium: true
  },
  fitExport: {
    name: 'FIT File Export',
    description: 'Basic and Premium subscribers can download their routes as FIT files. FIT files are smaller, process faster, and provide the best navigation experience with Garmin devices.',
    assetType: 'png',
    helpPath: '/help/export-file-formats'
  },
  pinning: {
    name: 'Pinning',
    description: 'There are so many routes, rides, rider profiles, and gear on our site it’s no doubt you’ll stumble upon something you don’t want to forget. We know the feeling, and we’re here to help you remember! Just pin whatever you want to keep track of and you’ll easily be able to locate it later on both the mobile app and website.',
    assetType: 'png',
    helpPath: 'https://ridewithgps.com/help/pinning-and-sharing'
  }
}

const context = require.context('./img', false, /.*/)

const asset = (name, ext) => context(`./${name}.${ext}`)

export class UpgradeModal extends React.PureComponent {
  render () {
    const {onRequestClose, showUpgradeButton} = this.props
    const hasUser = !!_.get(window, 'rwgps.config.currentUser.id')
    const upgradeButton = showUpgradeButton === undefined ? true : showUpgradeButton
    const {feature} = this.props
    const data = featureData[feature]
    if (!data) { throw Error(`Unknown UpgradeModal feature "${feature}"`) }
    const {name, description, assetType, helpPath} = data
    const upgradePath = data.premium ? '/subscriptions/new?account_level=premium' : '/choose_account?utm_campaign=choose_account&utm_medium=generic&utm_source=upsell'
    const video = (assetType === 'webm')

    const redirectParam = `redirect_to=${encodeURIComponent(window.location.href)}`

    const bigCTA = hasUser
      ? <CTA href={upgradePath}>Upgrade Now</CTA>
      : <CTA href={`/signup?${redirectParam}`}>Create an Account</CTA>

    return (
      <ModalOverlay contentPadding={false} maxWidth='875px' onRequestClose={onRequestClose} shouldCloseOnOverlayClick>
        <div className={styles.UpgradeModal}>
          <div className={styles.assetContainer}>
            {video &&
              <video autoPlay loop>
                <source src={asset(feature, 'mp4')} type={`video/mp4`} /> }
                <source src={asset(feature, 'webm')} type={`video/webm`} /> }
              </video>}
            { !video && <img src={asset(feature, assetType)} /> }
          </div>
          <div className={styles.descriptionContainer}>
            <h2 className={styles.title}> {name} </h2>
            <p className={styles.description}>{description}</p>
            { upgradeButton && bigCTA }
            <a className={styles.learnMore} href={helpPath}> Learn more about {name} </a>
            {getCurrentUser().eligibleForOnetimeTrial && <Fragment>
              <p>Interested in a 7 day trial of all Premium features? No credit card required!</p>
              <CTA secondary href={`/start_trial?${redirectParam}`}>Start Trial Now</CTA>
            </Fragment>}
          </div>
        </div>
      </ModalOverlay>
    )
  }
}
